import { Injectable } from '@angular/core';
import { Observable, catchError, forkJoin, map, of, tap } from 'rxjs';
import { FilterDTO, FilterOperator, ParkingSubscriptionClient, ParkingSubscriptionDTO, ParkingTariffClient, ParkingTariffDTO, SortDirection, ZoneClient, ZoneDTO, ZoneDeviceClient } from 'src/app/base/models/models.swagger.generated';
import { ClientService } from 'src/app/base/services/client/client.service';
import { ToastService } from 'src/app/base/services/toast.service';
import { IAdaptorService } from 'src/app/base/shared/syncfusion/adaptor-service.interface';
import { IPagedItems } from 'src/app/base/shared/syncfusion/paged-items.interface';
import { SpinnerService } from 'src/app/base/spinner/spinner.service';
import { ItemIdUtil } from '../../item-id.util';
import { AdministrationItemType, DetailsInitInfo, IItemData, ITreeNodeData } from '../../data-structures';
import { ErrorType } from 'src/app/core/routing/error-types';
import { ItemCache } from '../item-cache';

@Injectable({
  providedIn: 'root'
})
export class TreeViewZoneBasedService implements IAdaptorService {

  public itemCache = new ItemCache([AdministrationItemType.SubscriptionCollection, AdministrationItemType.ZoneCollection, AdministrationItemType.ZoneDeviceCollection, AdministrationItemType.ZoneDevice]);

  private nodeIdCnt = 0;

  constructor(
    private zoneClient: ZoneClient,
    private clientService: ClientService,
    private tariffClient: ParkingTariffClient,
    private zoneDeviceClient: ZoneDeviceClient,
    private parkingSubscriptionClient: ParkingSubscriptionClient,
    private spinnerService: SpinnerService,
    private toastService: ToastService) {}

  public getData(skip: number, take: number, filters: FilterDTO[], sortBy: string, sortDirection: SortDirection): Observable<IPagedItems> {
    const clientId = this.clientService.selectedClient?.id;
    let parentType = null;
    let parentId: number = null;
    if (!filters) {
      this.itemCache.resetCache();
      filters = [];
      filters.push(new FilterDTO({
        isComplex: false,
        field: 'ParentId',
        operator: FilterOperator.IsNull,
      }));
    } else {
      const parentIdfilter = filters.find((filter) => filter.field === 'ParentId');
      if (parentIdfilter) {
        const parsedData = this.parseNodeId(parentIdfilter.value);
        parentIdfilter.value = parsedData.id?.toString();
        parentId = parsedData.id;
        if (parentIdfilter.value == null) {
          parentIdfilter.operator = FilterOperator.IsNull;
          this.itemCache.resetCache();
        }
        parentType = parsedData.type;
      }
    }
    if (parentType == null) {
      const mappedItems: IPagedItems = {items: [], totalNumberOfItems: 1};
      let id = this.getNextNodeId(null, AdministrationItemType.ZoneCollection);
      mappedItems.items.push({
        id,
        hasChildren: true,
        parentId: null,
        expanded: true,
        itemData: {
          type: AdministrationItemType.ZoneCollection,
          name: 'i18n.domain.administration.overview.zones',
          inactive: false,
          dto: null,
        }
      });
      id = this.getNextNodeId(null, AdministrationItemType.SubscriptionCollection);
      mappedItems.items.push({
        id,
        hasChildren: true,
        parentId: null,
        expanded: true,
        itemData: {
          type: AdministrationItemType.SubscriptionCollection,
          name: 'i18n.domain.administration.overview.unassigned-subscriptions',
          inactive: false,
          dto: null,
        }
      });
      id = this.getNextNodeId(null, AdministrationItemType.TariffCollection);
      mappedItems.items.push({
        id,
        hasChildren: true,
        parentId: null,
        expanded: true,
        itemData: {
          type: AdministrationItemType.TariffCollection,
          name: 'i18n.domain.administration.overview.unassigned-tariffs',
          inactive: false,
          dto: null,
        }
      });
      return of(mappedItems);
    }
    if (parentType == AdministrationItemType.ZoneCollection) {
      return this.zoneClient.searchZones(clientId, filters, 0, 0, 'name', SortDirection.Ascending).pipe(
        map((pagedItems) => {
          const mappedItems: IPagedItems = {items: [], totalNumberOfItems: pagedItems.totalNumberOfItems};
          pagedItems.items.forEach((item) => {
            const mappedItem: ITreeNodeData = {
              id: this.getNextNodeId(item.id, AdministrationItemType.Zone),
              hasChildren: true,
              itemData: {
                type: AdministrationItemType.Zone,
                name: item.name,
                inactive: false,
                dto: item
              }
            }
            mappedItems.items.push(mappedItem);
            this.itemCache.addOrUpdateItem(mappedItem.itemData);
          });
          return mappedItems;
        })
      );
    }
    if (parentType == AdministrationItemType.Zone) {
      const zoneInfo = this.itemCache.getCachedItem(parentId, AdministrationItemType.Zone)?.itemData?.dto as ZoneDTO;
      let tariffObservable = this.tariffClient.getParkingTariffsForZone(parentId).pipe(
        map((tariffs) => {
          const items = [];
          tariffs.forEach((item) => {
            let itemData: IItemData = {
              type: AdministrationItemType.Tariff,
              name: item.name,
              inactive: !item.active,
              dto: item
            };
            // the same tariff can be assigned to different zones and therefore its data has to be shared in the tree.
            itemData = this.itemCache.addOrUpdateItem(itemData).itemData;
            const mappedItem: ITreeNodeData = {
              id: this.getNextNodeId(item.id, AdministrationItemType.Tariff),
              hasChildren: item.subscriptionId > 0,
              itemData
            }
            items.push(mappedItem);
          });
          return items;
        }
      ));
      let zoneObservable = this.zoneClient.searchZones(clientId, filters, 0, 0, 'name', SortDirection.Ascending).pipe(
        map((pagedItems) => {
          const items = [];
          pagedItems.items.forEach((item) => {
            const mappedItem: ITreeNodeData = {
              id: this.getNextNodeId(item.id, AdministrationItemType.Zone),
              hasChildren: true,
              itemData: {
                type: AdministrationItemType.Zone,
                name: item.name,
                inactive: false,
                dto: item
              }
            }
            items.push(mappedItem);
            this.itemCache.addOrUpdateItem(mappedItem.itemData);
          });
          return items;
        })
      );

      let deviceObservable = of([]);
      if (zoneInfo) {
        let deviceCollectionItems = [];
        deviceCollectionItems.push({
          id: this.getNextNodeId(parentId, AdministrationItemType.ZoneDeviceCollection),
          hasChildren: zoneInfo.hasDevicesAssigned,
          itemData: {
            type: AdministrationItemType.ZoneDeviceCollection,
            name: 'i18n.domain.administration.overview.devices',
            inactive: false,
            dto: null
          }
        });
        deviceObservable = of(deviceCollectionItems);
      }

      // first device folder is added then zones and then tariffs -> so keep an eye on the correct order
      return forkJoin([deviceObservable, tariffObservable, zoneObservable]).pipe(
        map(input => {
          const mappedItems: IPagedItems = {items: [].concat(...input), totalNumberOfItems: 1};
          return mappedItems;
        })
      );
    }
    if (parentType == AdministrationItemType.ZoneDeviceCollection) {
      filters = [new FilterDTO({
        isComplex: false,
        field: 'ZoneId',
        operator: FilterOperator.Equal,
        value: parentId?.toString()
      })];
      return this.zoneDeviceClient.searchDevices(clientId, filters, 0, 0, 'name', SortDirection.Ascending).pipe(
        map((pagedItems) => {
          const mappedItems: IPagedItems = {items: [], totalNumberOfItems: pagedItems.totalNumberOfItems};
          pagedItems.items.forEach((item) => {
            const mappedItem: ITreeNodeData = {
              id: this.getNextNodeId(item.id, AdministrationItemType.ZoneDevice),
              hasChildren: false,
              itemData: {
                type: AdministrationItemType.ZoneDevice,
                name: item.name,
                inactive: !item.enabled,
                dto: item
              }
            }
            mappedItems.items.push(mappedItem);
            this.itemCache.addOrUpdateItem(mappedItem.itemData);
          });
          return mappedItems;
        })
      );
    }
    if (parentType == AdministrationItemType.Tariff) {
      const tariffInfo = this.itemCache.getCachedItem(parentId, AdministrationItemType.Tariff)?.itemData?.dto as ParkingTariffDTO;
      if (!tariffInfo?.subscriptionId) {
        return of({items: [], totalNumberOfItems: 0});
      }
      return this.parkingSubscriptionClient.getParkingSubscription(tariffInfo.subscriptionId).pipe(
        map((subscription) => {
          const mappedItems: IPagedItems = {items: [], totalNumberOfItems: 1};
          let itemData: IItemData = {
            type: AdministrationItemType.Subscription,
            name: subscription.name,
            inactive: !subscription.active || !subscription.assignable,
            dto: subscription
          };
          // the same subscription can be assigned to different tariffs and therefore its data has to be shared in the tree.
          itemData = this.itemCache.addOrUpdateItem(itemData).itemData;
          const mappedItem: ITreeNodeData = {
            id: this.getNextNodeId(subscription.id, AdministrationItemType.Subscription),
            hasChildren: false,
            itemData
          }
          mappedItems.items.push(mappedItem);
          return mappedItems;
        })
      );
    }
    if (parentType == AdministrationItemType.TariffCollection) {
      const tariffFilter = [
        new FilterDTO({
          isComplex: false,
          field: 'ParkingTariffZoneAssignments',
          operator: FilterOperator.None,
        })
      ];
      return this.tariffClient.searchParkingTariffs(clientId, tariffFilter, 0, 0, 'name', SortDirection.Ascending, true).pipe(map((pagedItems) => {
        const mappedItems: IPagedItems = {items: [], totalNumberOfItems: pagedItems.totalNumberOfItems};
        pagedItems.items.forEach((item) => {
          const mappedItem: ITreeNodeData = {
            id: this.getNextNodeId(item.id, AdministrationItemType.Tariff),
            hasChildren: item.subscriptionId > 0,
            itemData: {
              type: AdministrationItemType.Tariff,
              name: item.name,
              inactive: !item.active,
              dto: item
            }
          }
          mappedItems.items.push(mappedItem);
          this.itemCache.addOrUpdateItem(mappedItem.itemData);
        });
        return mappedItems;
      })
      );
    }
    if (parentType == AdministrationItemType.SubscriptionCollection) {
      const subscriptionFilter = [
        new FilterDTO({
          isComplex: false,
          field: 'Tariffs',
          operator: FilterOperator.None,
        })
      ];
      return this.parkingSubscriptionClient.searchParkingSubscriptions(clientId, subscriptionFilter, 0, 0, 'name', SortDirection.Ascending).pipe(map((pagedItems) => {
        const mappedItems: IPagedItems = {items: [], totalNumberOfItems: pagedItems.totalNumberOfItems};
        pagedItems.items.forEach((item) => {
          const mappedItem: ITreeNodeData = {
            id: this.getNextNodeId(item.id, AdministrationItemType.Subscription),
            hasChildren: false,
            itemData: {
              type: AdministrationItemType.Subscription,
              name: item.name,
              inactive: !item.active || !item.assignable,
              dto: item
            }
          }
          mappedItems.items.push(mappedItem);
          this.itemCache.addOrUpdateItem(mappedItem.itemData);
        });
        return mappedItems;
      })
      );
    }
    // default
    return of({items: [], totalNumberOfItems: 0});
  }

  public addRecord(data: any): Observable<any> {
    // this is just to make tree view happy, so it will show the node in the tree
    return of(data);
  }

  public updateRecord(data: any): Observable<any> {
    // this is just to make tree view happy, so it will show the node in the tree
    return of(data);
  }

  public deleteRecord(id: number): Observable<any> {
    // this is just to make tree view happy, so it will show the node in the tree
    return of(id);
  }

  public detachTariffFromSubscription(tariffId: number, subscriptionId): Observable<void> {
    this.spinnerService.show();
    return this.tariffClient.assignNewSubscriptionToTariff(tariffId, null).pipe(
      tap(() => {
        const cachedItem = this.itemCache.getCachedItem(subscriptionId, AdministrationItemType.Subscription);
        const subscription = cachedItem?.itemData?.dto as ParkingSubscriptionDTO;
        if (subscription) {
          const index = subscription.tariffs.findIndex((tariff) => tariff.id === tariffId);
          subscription.tariffs.splice(index, 1);
        }
        this.spinnerService.hide();
        this.toastService.showSuccess('i18n.domain.administration.overview.messages.subscription-detach-title', 'i18n.domain.administration.overview.messages.subscription-detach-success');
      }),
      catchError((error: any) => {
        this.spinnerService.hide();
        this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
        return of(null);
      })
    );
  }

  public moveZoneDevice(deviceId: number, targetZoneId: number) {
    this.spinnerService.show();
    return this.zoneDeviceClient.moveDevice(deviceId, targetZoneId).pipe(
      tap(() => {
        this.moveItemSuccess();
      }),
      catchError((error: any) => {
        return this.handleItemError();
      })
    );
  }

  public moveZone(zoneId: number, targetZoneId: number) {
    this.spinnerService.show();
    return this.zoneClient.moveZone(zoneId, targetZoneId).pipe(
      tap(() => {
        this.moveItemSuccess();
      }),
      catchError((error: any) => {
        return this.handleItemError();
      })
    );
  }

  public addTariffToZone(tariffId: number, targetZoneId: number): Observable<ITreeNodeData> {
    this.spinnerService.show();
    return this.tariffClient.moveTariffToNewZone(tariffId, null, targetZoneId).pipe(
      map((dto) => {
        this.addItemSuccess();
        let itemData: IItemData = {
          type: AdministrationItemType.Tariff,
          name: dto.name,
          inactive: !dto.active,
          dto: dto
        };
        // the same tariff can be assigned to different zones and therefore its data has to be shared in the tree.
        itemData = this.itemCache.addOrUpdateItem(itemData).itemData;
        const mappedItem: ITreeNodeData = {
          id: this.getNextNodeId(dto.id, AdministrationItemType.Tariff),
          hasChildren: dto.subscriptionId > 0,
          itemData
        }
        return mappedItem;
      }),
      catchError((error: any) => {
        return this.handleItemError();
      })
    );
  }

  public moveTariff(tariffId: number, zoneId: number, targetZoneId: number): Observable<any> {
    this.spinnerService.show();
    return this.tariffClient.moveTariffToNewZone(tariffId, zoneId, targetZoneId).pipe(
      tap((updatedTariff) => {
        const cachedItem = this.itemCache.getCachedItem(tariffId, AdministrationItemType.Tariff);
        const itemData = cachedItem?.itemData;
        if (itemData) {
          itemData.dto = updatedTariff;
        }
        if (targetZoneId) {
          this.moveItemSuccess();
        } else {
          this.removeItemSuccess();
        }
      }),
      catchError((error: any) => {
        return this.handleItemError();
      })
    );
  }

  public addSubscriptionToTariff(subscriptionId: number, targetTariffId: number): Observable<ITreeNodeData> {
    this.spinnerService.show();
    return this.parkingSubscriptionClient.moveSubscriptionToNewTariff(subscriptionId, null, targetTariffId).pipe(
      map((dto) => {
        this.addItemSuccess();
        let itemData: IItemData = {
          type: AdministrationItemType.Subscription,
          name: dto.name,
          inactive: !dto.active || !dto.assignable,
          dto: dto
        };
        // the same subscription can be assigned to different tariffs and therefore its data has to be shared in the tree.
        itemData = this.itemCache.addOrUpdateItem(itemData).itemData;
        const mappedItem: ITreeNodeData = {
          id: this.getNextNodeId(dto.id, AdministrationItemType.Subscription),
          hasChildren: false,
          itemData
        }
        return mappedItem;
      }),
      catchError((error: any) => {
        return this.handleItemError();
      })
    );
  }

  public moveSubscription(subscriptionId: number, tariffId: number, targetTariffId: number) {
    this.spinnerService.show();
    return this.parkingSubscriptionClient.moveSubscriptionToNewTariff(subscriptionId, tariffId, targetTariffId).pipe(
      tap((dto) => {
        const cachedItem = this.itemCache.getCachedItem(subscriptionId, AdministrationItemType.Subscription);
        const itemData = cachedItem?.itemData;
        if (itemData) {
          itemData.dto = dto;
        }
        this.moveItemSuccess();
      }),
      catchError((error: any) => {
        return this.handleItemError();
      })
    );
  }

  public getNextNodeId(itemId: number, type: AdministrationItemType): string {
    this.nodeIdCnt++;
    return ItemIdUtil.createItemId(itemId, type, this.nodeIdCnt);
  }

  public parseNodeId(nodeId: string): DetailsInitInfo {
    return ItemIdUtil.parseItemId(nodeId) as DetailsInitInfo;
  }

  private addItemSuccess(): void {
    this.spinnerService.hide();
    this.toastService.showSuccess('i18n.domain.administration.overview.messages.item-added-title', 'i18n.domain.administration.overview.messages.item-added-success');
  }

  private moveItemSuccess(): void {
    this.spinnerService.hide();
    this.toastService.showSuccess('i18n.domain.administration.overview.messages.item-moved-title', 'i18n.domain.administration.overview.messages.item-moved-success');
  }

  private removeItemSuccess(): void {
    this.spinnerService.hide();
    this.toastService.showSuccess('i18n.domain.administration.overview.messages.item-removed-title', 'i18n.domain.administration.overview.messages.item-removed-success');
  }

  private handleItemError(): Observable<any> {
    this.spinnerService.hide();
    this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
    return of(null);
  }
}
